import axios from 'axios';
import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	InputLabel,
	Link,
	ListSubheader,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import SelectorsEdit from './SelectorsEdit';
import SelectorsNew from './SelectorsNew';
import React from 'react';
import { required, TextInput, useNotify, useUpdate } from 'react-admin';
import { useRecordContext } from 'react-admin';
import ExpectedValuesNewEdit from './ExpectedValuesNewEdit';
import {
	CRAWLING_METHOD_VALUES,
	DEFAULT_MODEL,
	useSchemaSelectorFinderSession
} from '../create/useSchemaSelectorFinderSession';
import Chat from '../create/Chat';
import { ConversationState } from '../create/WSCommunicationTypes';

function SetSelectorsForm(props: any) {
	const record = useRecordContext();
	const notify = useNotify();
	const [update, { isLoading }] = useUpdate();
	const { getValues, setValue, watch } = useFormContext();
	const [searchVariant, setSearchVariant] = useState('schema');
	const watchedProductUrl = watch('productUrl');
	const interceptedSetValue = useCallback(
		(name: string, value: string) => {
			if (name.startsWith('selector')) {
				setValue(name.replace('selector', 'newSelector'), value);
			} else if (name.startsWith('actual')) {
				if (!value.startsWith('Error: ')) {
					setValue(name.replace('actual', 'newExpected'), value);
				}
			} else {
				setValue(name, value);
			}
		},
		[setValue]
	);
	const {
		productUrl,
		aiModel,
		canSearch,
		chatMessages,
		conversationState,
		crawlingMethod,
		customAIModel,
		handlePauseConversation,
		handleSearch: handleSearchAI,
		handleUserMessage,
		isSearching,
		setAIModel,
		setCrawlingMethod,
		setCustomAIModel,
		setProductUrl,
		setUseAI,
		handleDownloadHtml,
		useAI,
		isDownloadingHtml,
		availableModels,
	} = useSchemaSelectorFinderSession({
		getValues,
		setValue: interceptedSetValue,
		watchedProductUrl
	});

	const [isLoadingSchema, setIsLoadingSchema] = useState(false);
	const [, setIsLoadingSelectors] = useState(false);
	useEffect(() => {
		if (isSearching !== isLoadingSchema) {
			setIsLoadingSchema(isSearching);
		}
	}, [isLoadingSchema, isSearching]);
	const watchedCrawlingMethod = watch('method');
	useEffect(() => {
		if (crawlingMethod === '') {
			setCrawlingMethod(watchedCrawlingMethod);
		}
	}, [crawlingMethod, setCrawlingMethod, watchedCrawlingMethod]);
	const handleSearchSchema = () => {
		setIsLoadingSchema(true);
		const formData = getValues();

		axios
			.post(
				process.env.REACT_APP_HUB_API_URL +
					'/shops/get-expected-values-and-schema-selectors',
				{ formData: formData },
				{ withCredentials: true }
			)
			.then((response) => {
				for (const property in response.data.selectors) {
					if (property === 'breadCrumbs') {
						if (formData['breadCrumbs'] == null)
							setValue(
								'newSelectorBreadcrumbs',
								response.data.selectors['breadCrumbs']
							);
					} else {
						const name =
							'newSelector' +
							property.charAt(0).toUpperCase() +
							property.slice(1);
						if (formData[name] == null)
							setValue(name, response.data.selectors[property]);
					}
				}
				for (const property in response.data.expectedResponses) {
					if (property === 'breadCrumbs') {
						setValue(
							'newExpectedBreadcrumbs',
							response.data.expectedResponses['breadCrumbs']
								?.toString()
								.slice(0, 255)
						);
					} else {
						setValue(
							'newExpected' +
								property.charAt(0).toUpperCase() +
								property.slice(1),
							response.data.expectedResponses[property]
								?.toString()
								.slice(0, 255)
						);
					}
				}
			})
			.catch((error) => {
				console.log(error);
			})
			.finally(() => setIsLoadingSchema(false));
	};

	const handleSearchCSS = () => {
		setIsLoadingSelectors(true);
		const formData = getValues();

		axios
			.post(
				process.env.REACT_APP_HUB_API_URL +
					'/shops/get-selectors-by-expected-values',
				{ formData: formData },
				{ withCredentials: true }
			)
			.then((response) => {
				for (const property in response.data) {
					if (property === 'breadCrumbs') {
						if (formData['breadCrumbs'] == null)
							setValue(
								'newSelectorBreadcrumbs',
								response.data['breadCrumbs']
									?.toString()
									.slice(0, 255)
							);
					} else {
						const name =
							'new' +
							property.charAt(0).toUpperCase() +
							property.slice(1);
						if (formData[name] == null)
							setValue(
								name,
								response.data[property]
									?.toString()
									.slice(0, 255)
							);
					}
				}
			})
			.catch((error) => {
				console.log(error);
			})
			.finally(() => {
				setIsLoadingSelectors(false);
			});
	};

	const handleSearch = () => {
		switch (searchVariant) {
			case 'schema':
				handleSearchSchema();
				break;
			case 'css':
				handleSearchCSS();
				break;
			case 'ai':
				handleSearchAI();
				break;
		}
	};

	const switchAllValues = () => {
		const formData = getValues();
		for (const key in formData) {
			if (key.startsWith('newSelector')) {
				const columnVal = getValues(key);
				if (columnVal) {
					const name = 's' + key.slice(4);
					setValue(name, columnVal);
				}
			}
			if (key.startsWith('newExpected')) {
				const columnVal = getValues(key);
				if (columnVal) {
					const name = 'e' + key.slice(4);
					setValue(name, columnVal);
				}
			}
		}
	};

	const handleSave = () => {
		const formData = getValues();
		debugger;
		update('shops', {
			id: formData.id,
			data: formData,
			previousData: record
		});
		props.close();
		notify('Neue Selektoren wurden gespeichert!', { type: 'success' });
	};

	return (
		<Box>
			<Box sx={{ backgroundColor: '#d9d9d9' }} p={1} my={2} pb={2}>
				<Box display={'flex'} justifyContent={'space-between'}>
					<Box display={'flex'} flexDirection={'column'}>
						<Box>
							<Stack direction='row'>
								<FormControl>
									<FormLabel id='search-variant-radio-buttons-group-label'>
										Such-Art
									</FormLabel>
									<RadioGroup
										row
										aria-labelledby='search-variant-radio-buttons-group-label'
										name='search-variant-row-radio-buttons-group'
										value={searchVariant}
										onChange={(e) => {
											setUseAI(e.target.value === 'ai');
											setSearchVariant(e.target.value);
										}}
									>
										<FormControlLabel
											value='schema'
											control={<Radio />}
											label='Schema-Selektoren'
											disabled={isLoadingSchema}
										/>
										<FormControlLabel
											value='css'
											control={<Radio />}
											label='CSS-Selektoren'
											disabled={isLoadingSchema}
										/>
										<FormControlLabel
											value='ai'
											control={<Radio />}
											label='KI'
											disabled={isLoadingSchema}
										/>
									</RadioGroup>
								</FormControl>
								<Box
									display={'flex'}
									flexDirection={'column'}
									justifyContent={'center'}
								>
									<Stack>
										<Button
											variant={'contained'}
											sx={{ mr: 2 }}
											onClick={handleSearch}
											disabled={
												isLoadingSchema ||
												(searchVariant === 'ai' &&
													!canSearch)
											}
										>
											Suchen{' '}
											{isLoadingSchema && (
												<CircularProgress
													size={20}
													sx={{ ml: 2 }}
												/>
											)}
										</Button>
									</Stack>
								</Box>
							</Stack>
						</Box>
						<Typography variant={'subtitle2'} sx={{ marginTop: 1 }}>
							<strong>Hinweis:</strong> Es werden nur nicht
							ausgefüllte Felder in der Spalte "Gefundene
							Selektoren" ersetzt
						</Typography>
					</Box>

					<Button
						variant={'contained'}
						color={'warning'}
						sx={{ ml: 2, height: 'fit-content' }}
						onClick={switchAllValues}
					>
						<strong>Alle gefundenen Selektoren übernehmen</strong>
					</Button>
					<Button
						variant={'contained'}
						color={'success'}
						sx={{ ml: 2, height: 'fit-content' }}
						onClick={handleSave}
						disabled={isLoading}
					>
						Neue Selektoren für diesen Shop speichern!{' '}
						{isLoading && (
							<CircularProgress size={20} sx={{ ml: 2 }} />
						)}
					</Button>
				</Box>
				{searchVariant === 'ai' && (
					<Box>
						<Stack spacing={2}>
							<Stack
								spacing={2}
								direction='row'
								sx={{
									display: 'flex',
									justifyContent: 'space-between'
								}}
							>
								<Stack sx={{ flexGrow: 1 }}>
									<TextInput
										validate={[required()]}
										sx={{ maxWidth: 'sm', margin: 0 }}
										label='Produkt URL'
										source='productUrl'
										type='url'
										disabled={isSearching}
										onChange={(e) =>
											setProductUrl(e.target.value)
										}
									/>
									<FormControl fullWidth sx={{ margin: 0 }}>
										<InputLabel id='crawling-method-label'>
											Crawling-Methode
										</InputLabel>
										<Select
											id='crawling-method'
											value={crawlingMethod}
											label='Crawling-Methode'
											labelId='crawling-method-label'
											onChange={(e) =>
												setCrawlingMethod(
													e.target.value
												)
											}
											sx={{ maxWidth: 'sm' }}
											disabled={isSearching}
										>
											{CRAWLING_METHOD_VALUES.map(
												(method) => (
													<MenuItem
														key={method}
														value={method}
													>
														{method || 'curl'}
													</MenuItem>
												)
											)}
										</Select>
									</FormControl>
								</Stack>
								<Stack
									sx={{
										display: 'flex',
										justifyContent: 'flex-end'
									}}
								>
									<Button
										onClick={() => handleDownloadHtml()}
										disabled={!productUrl}
									>
										Download HTML{' '}
										{isDownloadingHtml && (
											<CircularProgress
												size={20}
												sx={{ ml: 2 }}
											/>
										)}
									</Button>
								</Stack>
							</Stack>
							<Stack direction='row' spacing={2}>
								<FormControl fullWidth>
									<InputLabel id='ai-model-label'>
										KI-Modell
									</InputLabel>
									<Select
										id='ai-model'
										value={aiModel}
										labelId='ai-model-label'
										label='KI-Modell'
										onChange={(e) =>
											setAIModel(e.target.value)
										}
										sx={{ maxWidth: 'sm' }}
										disabled={isSearching}
									>
										<MenuItem
											value={DEFAULT_MODEL}
										>{`Default (${DEFAULT_MODEL})`}</MenuItem>
										<MenuItem value='other'>
											Anderes Modell angeben
										</MenuItem>
										<ListSubheader>
											Alle Modelle
										</ListSubheader>
										{availableModels.filter(
											(m) => m !== DEFAULT_MODEL
										).map((model) => (
											<MenuItem key={model} value={model}>
												{model}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<Link
									href='https://developers.openai.com/api/docs/pricing'
									target='_blank'
									rel='noopener'
								>
									Modell-Pricing
								</Link>
							</Stack>
							{aiModel === 'other' && (
								<TextField
									type='text'
									placeholder={`z.B. ${DEFAULT_MODEL}`}
									value={customAIModel}
									onChange={(e) =>
										setCustomAIModel(e.target.value)
									}
									sx={{ maxWidth: 'sm' }}
									disabled={isSearching}
								/>
							)}
						</Stack>
					</Box>
				)}
			</Box>
			{searchVariant === 'ai' &&
				(conversationState !== undefined ||
					chatMessages.length > 0) && (
					<>
						<Divider sx={{ mb: 4, mt: 2 }} />
						<Chat
							chatStatus={
								conversationState === undefined
									? ConversationState.Other
									: conversationState
							}
							chatMessages={chatMessages}
							onSendUserMessage={
								useAI ? handleUserMessage : undefined
							}
							onPauseConversation={
								useAI ? handlePauseConversation : undefined
							}
							sx={{ marginBottom: 3 }}
						/>
					</>
				)}
			<Divider sx={{ mb: 4, mt: 2 }} />
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={4}>
						<SelectorsEdit label={'Aktuelle Selektoren'} />
					</Grid>
					<Grid item xs={4} sx={{ borderRight: '2px solid', pr: 2 }}>
						<SelectorsNew label={'Gefundene Selektoren'} />
					</Grid>
					<Grid item xs={4}>
						<ExpectedValuesNewEdit />
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}

export default SetSelectorsForm;
